import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Box, Checkbox, LinearProgress, MenuItem, Select } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { usePersistedState } from '../../../hooks/usePersistedState'
import { CRM_POLL, CrmSubjectRef, CrmTrackView, crmApi } from '../../../features/crm/crmApi'
import { filterField } from '../../../components/filterSelect/FilterSelect'
import { Card, CrmTimer, DetailsButton, Lightbox, Muted, PageHead, Pill, PrimaryButton, TONES, TableWrap, contentSx, pageSx } from '../shared/crmUi'
import { STEP_NOS, courseTitle, dictLabel, subjectTitle, whenShort } from '../shared/format'
import { trackDone } from '../shared/stages'
import HeadTrackInfo from './HeadTrackInfo'

// Выбор предмета басшы; один предмет - просто подпись
export const useHeadSubject = () => {
    const { data: subjects = [], isLoading } = crmApi.useGetHeadSubjectsQuery()
    const [saved, setSaved] = usePersistedState<number | null>('crm.headSubjectId', null)
    const subjectId = subjects.some((s) => s.id === saved) ? saved : subjects[0]?.id ?? null
    return { subjects, subjectId, setSubjectId: setSaved, subject: subjects.find((s) => s.id === subjectId), isLoading }
}

export const HeadSubjectSelect: React.FC<{ subjects: CrmSubjectRef[]; value: number | null; onChange: (id: number) => void }> = ({
    subjects,
    value,
    onChange,
}) => {
    const { formatMessage, locale } = useIntl()
    if (subjects.length < 2) return null
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontSize: '13px', fontWeight: 600 }}>{formatMessage({ id: 'crmSubjectLabel' })}</Box>
            <Select size="small" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} sx={{ ...filterField, minWidth: 200 }}>
                {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{subjectTitle(s, locale)}</MenuItem>
                ))}
            </Select>
        </Box>
    )
}

const HeadCrmNewPage = () => {
    const { formatMessage, locale } = useIntl()
    const hs = useHeadSubject()
    const { data: tracks = [], isFetching } = crmApi.useGetHeadTracksQuery(
        { subjectId: hs.subjectId ?? 0, tab: 'ALL' },
        { skip: !hs.subjectId, pollingInterval: CRM_POLL },
    )
    // только что назначенные остаются в верхней таблице с «Бекітілді ✓» до ухода со страницы
    const [justAssigned, setJustAssigned] = useState<number[]>([])
    const [open, setOpen] = useState<Record<string, boolean>>({})
    const [doneOpen, setDoneOpen] = useState(false)
    const [zoom, setZoom] = useState<string[] | null>(null)

    const fresh = useMemo(
        () => tracks.filter((t) => !t.mentorId || justAssigned.includes(t.id)).sort((a, b) => ((a.headAt || '') < (b.headAt || '') ? -1 : 1)),
        [tracks, justAssigned],
    )
    const work = useMemo(() => tracks.filter((t) => t.mentorId).sort((a, b) => ((a.assignedAt || '') < (b.assignedAt || '') ? 1 : -1)), [tracks])
    const workOpen = work.filter((t) => !trackDone(t))
    const workDone = work.filter(trackDone)
    const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }))
    const subjName = hs.subject ? subjectTitle(hs.subject, locale) : ''

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={`${formatMessage({ id: 'crmNewStudents' })}${subjName ? ` · ${subjName}` : ''}`}
                    sub={formatMessage({ id: 'crmHeadNewSub' })}
                    action={<HeadSubjectSelect subjects={hs.subjects} value={hs.subjectId} onChange={hs.setSubjectId} />}
                />
                {(isFetching || hs.isLoading) && <LinearProgress sx={{ marginBottom: 1 }} />}

                <Card pad={false}>
                    <TableWrap minWidth={860}>
                        <table>
                            <thead>
                                <tr>
                                    <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                    <th>{formatMessage({ id: 'crmLanguage' })}</th>
                                    <th>{formatMessage({ id: 'crmCourse' })}</th>
                                    <th>{formatMessage({ id: 'crmGroup' })}</th>
                                    <th>{formatMessage({ id: 'crmMentor' })}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {fresh.map((t) => (
                                    <React.Fragment key={t.id}>
                                        <NewRow
                                            t={t}
                                            open={!!open[`n${t.id}`]}
                                            onToggle={() => toggle(`n${t.id}`)}
                                            onAssigned={() => setJustAssigned((l) => [...l, t.id])}
                                        />
                                        {open[`n${t.id}`] && (
                                            <tr className="det">
                                                <td colSpan={6}>
                                                    <HeadTrackInfo t={t} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {!fresh.length && <EmptyRow cols={6} text={formatMessage({ id: 'crmNoNew' })} />}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>

                <PageHead title={formatMessage({ id: 'crmMentorTasks' })} sub={formatMessage({ id: 'crmMentorTasksSub' })} />
                <Card pad={false}>
                    <TasksTable rows={workOpen} open={open} toggle={toggle} onZoom={setZoom} empty={formatMessage({ id: 'crmNothingToVerify' })} />
                </Card>

                <Card pad={false}>
                    <Box
                        component="button"
                        type="button"
                        aria-expanded={doneOpen}
                        onClick={() => setDoneOpen(!doneOpen)}
                        sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            padding: '14px 16px',
                            cursor: 'pointer',
                            font: 'inherit',
                            fontSize: '14px',
                        }}
                    >
                        <b>✓ {formatMessage({ id: 'crmDoneList' })}</b>
                        <Pill tone="ok">{workDone.length}</Pill>
                        <Box component="span" sx={{ marginLeft: 'auto', color: ui.muted }}>{doneOpen ? '▴' : '▾'}</Box>
                    </Box>
                    {doneOpen && <TasksTable rows={workDone} open={open} toggle={toggle} onZoom={setZoom} empty={formatMessage({ id: 'crmNothingYet' })} short />}
                </Card>
                <Lightbox urls={zoom} onClose={() => setZoom(null)} />
            </Box>
        </Box>
    )
}

const NewRow: React.FC<{ t: CrmTrackView; open: boolean; onToggle: () => void; onAssigned: () => void }> = ({ t, open, onToggle, onAssigned }) => {
    const { formatMessage, locale } = useIntl()
    const assigned = !!t.mentorId
    const { data: groups = [] } = crmApi.useGetAssignOptionsQuery(t.id, { skip: assigned })
    const [assign, { isLoading }] = crmApi.useAssignTrackMutation()
    const [groupId, setGroupId] = useState<number | ''>('')
    const [mentorId, setMentorId] = useState<number | ''>('')
    const mentors = groups.find((g) => g.groupId === groupId)?.mentors ?? []

    const pickGroup = (id: number) => {
        setGroupId(id)
        const ms = groups.find((g) => g.groupId === id)?.mentors ?? []
        setMentorId(ms.length === 1 ? ms[0].mentorId : '')
    }

    const submit = async () => {
        if (!groupId || !mentorId) return
        try {
            await assign({ trackId: t.id, groupId: Number(groupId), mentorId: Number(mentorId) }).unwrap()
            onAssigned()
            toast.success(formatMessage({ id: 'crmAssignedToast' }))
        } catch {
            /* общий обработчик */
        }
    }

    const sel = { ...filterField, minWidth: 180, fontWeight: 500 }
    return (
        <tr>
            <td>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TimeChip iso={t.headAt} />
                    <b>{t.studentName}</b>
                </Box>
                <DetailsButton open={open} onClick={onToggle} />
            </td>
            <td>{dictLabel(formatMessage, 'Lang', t.language)}</td>
            <td>{courseTitle(t, locale)}</td>
            <td>
                {assigned ? (
                    t.groupTitle || '-'
                ) : (
                    <Select size="small" displayEmpty value={groupId} onChange={(e) => pickGroup(Number(e.target.value))} sx={sel}>
                        <MenuItem value="" disabled>{formatMessage({ id: 'crmPickGroup' })}</MenuItem>
                        {groups.map((g) => (
                            <MenuItem key={g.groupId} value={g.groupId}>{g.title}</MenuItem>
                        ))}
                    </Select>
                )}
            </td>
            <td>
                {assigned ? (
                    <Muted sx={{ fontSize: '13px' }}>{t.mentorName || '-'}</Muted>
                ) : (
                    <Select
                        size="small"
                        displayEmpty
                        disabled={!groupId}
                        value={mentorId}
                        onChange={(e) => setMentorId(Number(e.target.value))}
                        sx={sel}
                    >
                        <MenuItem value="" disabled>{formatMessage({ id: 'crmPickMentor' })}</MenuItem>
                        {mentors.map((m) => (
                            <MenuItem key={m.mentorId} value={m.mentorId}>
                                {formatMessage({ id: 'crmMentorOption' }, { name: m.mentorName || '-', n: m.studentCount })}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </td>
            <td className="r">
                {assigned ? (
                    <Pill tone="ok">{formatMessage({ id: 'crmAssignedOk' })}</Pill>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                        <PrimaryButton size="small" disabled={!groupId || !mentorId || isLoading} onClick={submit}>
                            {formatMessage({ id: 'crmAssign' })}
                        </PrimaryButton>
                        <CrmTimer deadlineAt={t.headDeadlineAt} closedAt={t.assignedAt} />
                    </Box>
                )}
            </td>
        </tr>
    )
}

const TasksTable: React.FC<{
    rows: CrmTrackView[]
    open: Record<string, boolean>
    toggle: (k: string) => void
    onZoom: (urls: string[]) => void
    empty: string
    short?: boolean
}> = ({ rows, open, toggle, onZoom, empty, short }) => {
    const { formatMessage } = useIntl()
    const [verify] = crmApi.useVerifyStepMutation()

    return (
        <TableWrap minWidth={980}>
            <table>
                <thead>
                    <tr>
                        <th>{formatMessage({ id: 'crmColStudent' })}</th>
                        <th>{formatMessage({ id: 'crmMentor' })}</th>
                        {STEP_NOS.map((no) => (
                            <th key={no} className="c">
                                {short ? no : `${no} · ${formatMessage({ id: `crmStepShort_${no}` })}`}
                            </th>
                        ))}
                        <th className="r">{formatMessage({ id: 'crmVerifiedCol' })}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((t) => (
                        <React.Fragment key={t.id}>
                            <tr>
                                <td>
                                    <b>{t.studentName}</b>
                                    {!t.finishedAt && (
                                        <Box sx={{ marginTop: '4px' }}>
                                            <CrmTimer deadlineAt={t.mentorDeadlineAt} />
                                        </Box>
                                    )}
                                    <Box>
                                        <DetailsButton open={!!open[`w${t.id}`]} onClick={() => toggle(`w${t.id}`)} />
                                    </Box>
                                </td>
                                <td>
                                    <Pill tone="pink">{t.mentorName || '-'}</Pill>
                                </td>
                                {STEP_NOS.map((no) => {
                                    const st = t.steps?.find((s) => s.stepNo === no)
                                    if (!st?.doneAt || !st.photos?.length) return <td key={no} className="c" style={{ color: ui.muted }}>-</td>
                                    const urls = st.photos.map((p) => p.url)
                                    const verified = !!st.verifiedAt
                                    const tip =
                                        no === 5
                                            ? `${formatMessage({ id: 'crmResult' })}: ${dictLabel(formatMessage, 'Fb', t.feedback)}${t.feedbackNote ? ` - ${t.feedbackNote}` : ''}`
                                            : undefined
                                    return (
                                        <td key={no} className="c">
                                            <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                                <img
                                                    src={urls[0]}
                                                    alt={formatMessage({ id: `crmStep_${no}` })}
                                                    title={tip}
                                                    onClick={() => onZoom(urls)}
                                                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in', border: `1px solid ${ui.border}` }}
                                                />
                                                {urls.length > 1 && (
                                                    <Box sx={{ position: 'absolute', right: -6, top: -6, background: ui.ink, color: '#fff', borderRadius: '10px', fontSize: '11px', padding: '0 6px' }}>
                                                        {urls.length}
                                                    </Box>
                                                )}
                                            </Box>
                                            <Box
                                                component="label"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    color: verified ? TONES.ok.text : ui.sub,
                                                    background: verified ? TONES.ok.bg : 'transparent',
                                                    borderRadius: '8px',
                                                    marginTop: '4px',
                                                    paddingRight: '6px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={verified}
                                                    sx={{ padding: '2px' }}
                                                    onChange={(e) => verify({ stepId: st.id, verified: e.target.checked })}
                                                />
                                                {formatMessage({ id: verified ? 'crmVerifiedDone' : 'crmVerify' })}
                                            </Box>
                                            {no === 5 && t.feedback && <Muted>{dictLabel(formatMessage, 'Fb', t.feedback)}</Muted>}
                                        </td>
                                    )
                                })}
                                <td className="r">
                                    <b style={{ color: trackDone(t) ? TONES.ok.text : ui.ink }}>{t.verifiedCount}/5</b>
                                </td>
                            </tr>
                            {open[`w${t.id}`] && (
                                <tr className="det">
                                    <td colSpan={8}>
                                        <HeadTrackInfo t={t} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                    {!rows.length && <EmptyRow cols={8} text={empty} />}
                </tbody>
            </table>
        </TableWrap>
    )
}

export const TimeChip: React.FC<{ iso?: string | null }> = ({ iso }) =>
    iso ? (
        <Box component="span" sx={{ fontSize: '12px', fontWeight: 700, color: ui.sub, background: TONES.grey.bg, borderRadius: '6px', padding: '2px 6px', whiteSpace: 'nowrap' }}>
            {whenShort(iso)}
        </Box>
    ) : null

export const EmptyRow: React.FC<{ cols: number; text: string }> = ({ cols, text }) => (
    <tr>
        <td colSpan={cols} style={{ color: ui.muted, padding: 24 }}>
            {/* таблица на телефоне шире экрана - держим текст в видимой части */}
            <Box sx={{ position: 'sticky', left: 0, textAlign: 'center', width: { xs: 'calc(100vw - 80px)', md: 'auto' } }}>{text}</Box>
        </td>
    </tr>
)

export default HeadCrmNewPage
